/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';

import {
  useGetDealerSetupFeatureConfigs,
  useUpdateDealerSetupFeatureConfigs,
} from 'hooks/api/dealerSetupFeatureConfigs';
import SidebarContainer from '../SidebarContainer';
import dealersActions from '../../redux/dealers/actions';
import feedsActions from '../../redux/feeds/actions';
import verificationsActions from '../../redux/verifications/actions';
import groupActions from '../../redux/groups/actions';
import websiteActions from '../../redux/websites/actions';
import feedviewsActions from '../../redux/feedviews/actions';
import ErrorMessage from '../../components/ErrorMessage';
import './Dealer.scss';
import LoadingOverlay from '../../components/LoadingOverlay';

import { DealerEditor, NewDealer } from '../../components/DealerEditor';
import ContentPage from '../ContentPage';
import DealerNav from '../../components/DealerNav';

export const DAY_LIST = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export const PICKUP_DEFAULTS = {
  appointmentWindow: '30',
  startsAfter: '12 hours',
  endsAfter: '5 days',
  days: {
    ...DAY_LIST.reduce(
      (prev, day) => ({
        ...prev,
        [day]: { enabled: !day.startsWith('s'), start: 10, end: 19 },
      }),
      {},
    ),
  },
};

export const DELIVERY_DEFAULTS = {
  enabled: false,
  startsAfter: '12 hours',
  endsAfter: '5 days',
  radius: 50,
  days: {
    ...DAY_LIST.reduce(
      (prev, day) => ({
        ...prev,
        [day]: { enabled: !day.startsWith('s'), start: 10, end: 19 },
      }),
      {},
    ),
  },
};

function Dealer(props) {
  const { dealers, actions, history, feeds, groups, feedviews, reduxFetching } =
    props;
  const { dealer, error } = dealers;
  const { dealerId, step } = props.match.params;
  const {
    feedsRequest,
    groupsRequest,
    createDealerRequest,
    updateDealerRequest,
    feedviewsRequest,
  } = actions;

  useEffect(() => {
    if (!error) {
      feedsRequest();
      groupsRequest();
      feedviewsRequest();
    }
  }, [error, feedsRequest, feedviewsRequest, groupsRequest]);

  const { data: featureConfigs, isValidating } =
    useGetDealerSetupFeatureConfigs(dealer.id);
  const { trigger: updateFeatureConfigs, isMutating: savingFeatureConfigs } =
    useUpdateDealerSetupFeatureConfigs(dealer.id);

  const fetching = reduxFetching || isValidating || savingFeatureConfigs;

  const onCreate = (params) =>
    // Default pickup and delivery are needed to display properly
    createDealerRequest({
      ...params,
      desking: { enabled: true, onlineDesking: true },
      marketScanConfig: {
        incentivesEnabled: true,
        paymentsEnabled: true,
        taxesEnabled: true,
        feesEnabled: true,
      },
      tabletFormsConfig: { customerInfo: { useAddCarWithoutVIN: true } },
      featureConfig: {
        useCPO: { enabled: true },
        multiDealerManagement: { enableCrossSellingFromPartnerStores: true },
      },
      wizard: {
        pickup: PICKUP_DEFAULTS,
        delivery: DELIVERY_DEFAULTS,
        enableProdigyWeb: { enabled: true },
        enableDocUpload: { enabled: true },
      },
    });

  const onSave = async (payload) => {
    if (payload.v2Payloads.featureConfigs) {
      // Do not run this in parallel with updateDealer on purpose.
      // At the time of writing both APIs update the dealer via a read-then-write, so we want
      // to avoid race conditions that may cause config updates to revert.
      await updateFeatureConfigs(payload.v2Payloads.featureConfigs);
    }

    updateDealerRequest({
      ...payload,
      route: `/dealer/${dealerId}`,
    });
  };

  const setStep = (s) => {
    props.history.push(`/dealer/${dealerId}/${s}`);
  };

  return (
    <SidebarContainer
      title={dealer.name ? dealer.name : 'Create a Dealer'}
      openSubMenu="Client Management"
      logo={dealer.logo}
    >
      <div className="dealers-table">
        {dealer.id && <DealerNav />}
        {error && <ErrorMessage error={error} />}
        {dealer.id && (
          <LoadingOverlay active={fetching}>
            <DealerEditor
              feeds={feeds}
              dealer={{ ...dealer, countryToggle: dealer.country }}
              featureConfigs={featureConfigs}
              groups={groups}
              fetching={fetching}
              step={parseInt(step, 10)}
              onSave={onSave}
              setStep={setStep}
              actions={actions}
              feedviews={feedviews}
              onInventoryImport={actions.inventoryImportRequest}
            />
          </LoadingOverlay>
        )}
        {!dealer.id && (
          <NewDealer
            dealer={dealer}
            fetching={fetching}
            history={history}
            onCreate={onCreate}
          />
        )}
      </div>
    </SidebarContainer>
  );
}

Dealer.propTypes = {
  actions: PropTypes.object,
  dealers: PropTypes.object,
  feeds: PropTypes.array,
  groups: PropTypes.array,
  history: PropTypes.object,
  match: PropTypes.object,
  feedviews: PropTypes.object,
  reduxFetching: PropTypes.bool,
};

const mapStateToProps = ({ dealers, feeds, groups, feedviews }) => ({
  dealers,
  feeds: feeds.feeds,
  groups: groups?.groups,
  feedviews: feedviews?.feedviews,
  reduxFetching:
    dealers.fetching || feeds.fetching || groups.fetching || feedviews.fetching,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...dealersActions,
      ...feedsActions,
      ...verificationsActions,
      ...groupActions,
      ...feedviewsActions,
      ...websiteActions,
    },
    dispatch,
  ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContentPage(Dealer));
